import { Box, Grid, Typography, Button, Card, CardContent, Modal, IconButton, Skeleton } from "@mui/material"
import { useEffect, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { HttpRequest } from "../HttpRequest";
import EvoucherCheckout from "./evoucherCheckout";
import { useSelector } from "react-redux";
import { LocalOffer, Close, CheckCircle } from '@mui/icons-material';
import { currencyconverter } from "../../utils/dateFormatter";
import { useNavigate } from "react-router-dom";

// Skeleton loader for voucher grid cards
const VoucherGridCardSkeleton = () => {
    const { colors } = useSelector((state) => state.template);
    const primaryColor = colors?.primary || '#02b3ad';

    return (
        <Card
            sx={{
                background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}08 100%)`,
                borderRadius: { xs: '16px', md: '20px' },
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.06)',
                overflow: 'hidden',
                height: '100%',
                position: 'relative',
                border: `2px solid ${primaryColor}20`,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardContent sx={{
                padding: { xs: '20px', sm: '24px' },
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}>
                {/* Badge Skeleton */}
                <Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: '20px' }} />

                {/* Campaign Name Skeleton */}
                <Skeleton variant="text" width="90%" height={32} sx={{ marginBottom: 1 }} />
                <Skeleton variant="text" width="70%" height={32} />

                {/* Description Skeleton */}
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="95%" height={20} />
                <Skeleton variant="text" width="85%" height={20} />

                {/* Countdown Timer Skeleton */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        marginTop: 'auto',
                    }}
                >
                    {[1, 2, 3, 4].map((item) => (
                        <Box key={item} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '8px' }} />
                            <Skeleton variant="text" width={36} height={14} />
                        </Box>
                    ))}
                </Box>

                {/* Button Skeleton */}
                <Skeleton variant="rounded" width="100%" height={46} sx={{ borderRadius: '5px', marginTop: 1 }} />
            </CardContent>
        </Card>
    );
};

const VoucherAmountModal = ({ open, onClose, voucher, onProceed }) => {
    const { colors, typography, buttons } = useSelector((state) => state.template);
    const [selectedAmount, setSelectedAmount] = useState(null);
    let { isLoggedIn } = useSelector((store) => store?.user);
    const navigate = useNavigate();

    const primaryColor = colors?.primary || '#02b3ad';
    const headingsColor = colors?.headings || '#343434';
    const bodyFont = typography?.body || 'Poppins';
    const headingsFont = typography?.headings || 'Poppins';
    const buttonPrimaryColor = buttons?.primary || primaryColor;
    const buttonRadius = buttons?.borderRadius || 5;

    useEffect(() => {
        // Auto-select if only one amount available
        if (voucher?.amounts?.length === 1) {
            setSelectedAmount(voucher.amounts[0]);
        } else {
            setSelectedAmount(null);
        }
    }, [voucher]);

    const handleProceed = () => {
        if (!isLoggedIn) {
            navigate('/login', { state: { previousPath: '/evouchers' } });
            return;
        }

        if (selectedAmount) {
            onProceed(selectedAmount);
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="voucher-amount-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    background: '#fff',
                    borderRadius: { xs: '16px', md: '20px' },
                    boxShadow: '0 24px 64px rgba(0, 0, 0, 0.15)',
                    maxWidth: { xs: '90%', sm: '500px', md: '600px' },
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    padding: { xs: '24px', sm: '32px', md: '40px' },
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: '#707070',
                        transition: 'all 0.3s',
                        '&:hover': {
                            color: primaryColor,
                            transform: 'rotate(90deg)',
                        },
                    }}
                >
                    <Close />
                </IconButton>

                {/* Header */}
                <Box sx={{ marginBottom: 3, textAlign: 'center' }}>
                    <LocalOffer
                        sx={{
                            fontSize: 48,
                            color: primaryColor,
                            marginBottom: 2,
                        }}
                    />
                    <Typography
                        sx={{
                            color: headingsColor,
                            fontSize: { xs: '22px', sm: '28px' },
                            fontWeight: 800,
                            marginBottom: 1,
                            fontFamily: headingsFont,
                        }}
                    >
                        {voucher?.name || voucher?.campaignName}
                    </Typography>
                    <Typography
                        sx={{
                            color: '#5a5a5a',
                            fontSize: '14px',
                            fontFamily: bodyFont,
                            lineHeight: 1.6,
                        }}
                    >
                        {voucher?.description}
                    </Typography>
                </Box>

                {/* Amount Selection */}
                <Box sx={{ marginBottom: 3 }}>
                    <Typography
                        sx={{
                            color: headingsColor,
                            fontSize: '16px',
                            fontWeight: 700,
                            marginBottom: 2,
                            fontFamily: bodyFont,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        Select Amount
                        <Box component="span" sx={{ color: '#ef0b0b' }}>*</Box>
                    </Typography>

                    <Grid container spacing={2}>
                        {voucher?.amounts?.map((amount) => (
                            <Grid item xs={6} sm={4} key={amount._id}>
                                <Button
                                    onClick={() => setSelectedAmount(amount)}
                                    fullWidth
                                    sx={{
                                        background: selectedAmount?._id === amount._id
                                            ? `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`
                                            : 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                                        color: selectedAmount?._id === amount._id ? '#fff' : '#707070',
                                        fontSize: { xs: '16px', sm: '18px' },
                                        fontWeight: 700,
                                        borderRadius: `${buttonRadius}px`,
                                        padding: { xs: '14px', sm: '16px' },
                                        border: selectedAmount?._id === amount._id
                                            ? `2px solid ${primaryColor}`
                                            : '2px solid #e0e0e0',
                                        boxShadow: selectedAmount?._id === amount._id
                                            ? `0 4px 16px ${primaryColor}40`
                                            : '0 2px 8px rgba(0, 0, 0, 0.06)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        position: 'relative',
                                        fontFamily: bodyFont,
                                        '&:hover': {
                                            background: selectedAmount?._id === amount._id
                                                ? `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`
                                                : 'linear-gradient(145deg, #fafafa 0%, #f0f0f0 100%)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: selectedAmount?._id === amount._id
                                                ? `0 6px 20px ${primaryColor}50`
                                                : '0 4px 12px rgba(0, 0, 0, 0.1)',
                                        },
                                    }}
                                >
                                    {currencyconverter(amount.voucherAmount)}
                                    {selectedAmount?._id === amount._id && (
                                        <CheckCircle
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                fontSize: 18,
                                            }}
                                        />
                                    )}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Info Box */}
                <Box
                    sx={{
                        background: `linear-gradient(135deg, ${primaryColor}10 0%, ${primaryColor}05 100%)`,
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: 3,
                        border: `1px solid ${primaryColor}30`,
                    }}
                >
                    <Typography
                        sx={{
                            color: '#5a5a5a',
                            fontSize: '13px',
                            fontFamily: bodyFont,
                            lineHeight: 1.6,
                        }}
                    >
                        <strong style={{ color: primaryColor }}>Note:</strong> Your eVoucher will be sent instantly to your email after successful payment. It can be used immediately at checkout.
                    </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        fullWidth
                        sx={{
                            color: '#707070',
                            borderColor: '#e0e0e0',
                            fontSize: '15px',
                            fontWeight: 600,
                            borderRadius: `${buttonRadius}px`,
                            padding: '14px',
                            textTransform: 'none',
                            fontFamily: bodyFont,
                            '&:hover': {
                                borderColor: '#c0c0c0',
                                background: 'rgba(0, 0, 0, 0.02)',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleProceed}
                        disabled={!selectedAmount}
                        variant="contained"
                        fullWidth
                        sx={{
                            background: !selectedAmount
                                ? 'linear-gradient(135deg, #e0e0e0 0%, #d0d0d0 100%)'
                                : `linear-gradient(135deg, ${buttonPrimaryColor} 0%, ${buttonPrimaryColor}dd 100%)`,
                            color: !selectedAmount ? '#999' : '#fff',
                            fontSize: '15px',
                            fontWeight: 700,
                            borderRadius: `${buttonRadius}px`,
                            padding: '14px',
                            textTransform: 'none',
                            fontFamily: bodyFont,
                            boxShadow: !selectedAmount
                                ? 'none'
                                : `0 8px 24px ${buttonPrimaryColor}40`,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                background: !selectedAmount
                                    ? 'linear-gradient(135deg, #e0e0e0 0%, #d0d0d0 100%)'
                                    : `linear-gradient(135deg, ${buttonPrimaryColor}dd 0%, ${buttonPrimaryColor} 100%)`,
                                transform: !selectedAmount ? 'none' : 'translateY(-2px)',
                                boxShadow: !selectedAmount
                                    ? 'none'
                                    : `0 12px 32px ${buttonPrimaryColor}50`,
                            },
                            '&:disabled': {
                                cursor: 'not-allowed',
                            },
                        }}
                    >
                        Proceed to Payment
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

const VoucherGridCard = ({ voucher, onSelectVoucher }) => {
    const { colors, typography, buttons } = useSelector((state) => state.template);

    // Use template colors with fallbacks
    const primaryColor = colors?.primary || '#02b3ad';
    const headingsColor = colors?.headings || '#343434';
    const bodyFont = typography?.body || 'Poppins';
    const headingsFont = typography?.headings || 'Poppins';
    const buttonPrimaryColor = buttons?.primary || primaryColor;
    const buttonRadius = buttons?.borderRadius || 5;

    const [timeRemaining, setTimeRemaining] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        const calculateTime = () => {
            const now = new Date().getTime();
            const countDownDate = new Date(voucher?.endDate).getTime();
            const differenceTime = countDownDate - now;

            if (differenceTime <= 0) {
                setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                setIsExpired(true);
                return true; // expired
            } else {
                const days = Math.floor(differenceTime / (1000 * 60 * 60 * 24));
                const hours = Math.floor((differenceTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((differenceTime % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((differenceTime % (1000 * 60)) / 1000);

                setTimeRemaining({ days, hours, minutes, seconds });
                setIsExpired(false);
                return false; // not expired
            }
        };

        // Initial calculation
        const expired = calculateTime();
        if (expired) return;

        // Set up interval
        const timer = setInterval(() => {
            const expired = calculateTime();
            if (expired) {
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [voucher?.endDate]);

    const totalHoursLeft = (timeRemaining.days * 24) + timeRemaining.hours;
    const isUrgent = totalHoursLeft < 24 && !isExpired;
    const isMediumUrgent = totalHoursLeft < 72 && totalHoursLeft >= 24;

    return (
        <Card
            sx={{
                background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}08 100%)`,
                borderRadius: { xs: '16px', md: '20px' },
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.06)',
                overflow: 'hidden',
                height: '100%',
                position: 'relative',
                border: `2px solid ${primaryColor}20`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                    boxShadow: '0 12px 48px 0 rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-8px)',
                    borderColor: `${primaryColor}40`,
                },
            }}
        >
            {/* Decorative elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -30,
                    right: -30,
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${primaryColor}15 0%, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />

            <CardContent sx={{
                padding: { xs: '20px', sm: '24px' },
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'relative',
                zIndex: 1,
            }}>
                {/* Badge */}
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        background: isExpired
                            ? 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)'
                            : isUrgent
                            ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                            : isMediumUrgent
                            ? 'linear-gradient(135deg, #ffd93d 0%, #ffb84d 100%)'
                            : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                        padding: '6px 14px',
                        borderRadius: '20px',
                        boxShadow: isExpired ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                        alignSelf: 'flex-start',
                    }}
                >
                    <LocalOffer sx={{ fontSize: '14px', color: '#fff' }} />
                    <Typography
                        sx={{
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.8px',
                            fontFamily: bodyFont,
                            textTransform: 'uppercase',
                        }}
                    >
                        {isExpired ? 'Expired' : isUrgent ? 'Ending Soon!' : 'Active'}
                    </Typography>
                </Box>

                {/* Campaign Name */}
                <Typography
                    sx={{
                        color: headingsColor,
                        fontSize: { xs: '20px', sm: '24px' },
                        fontWeight: 800,
                        lineHeight: 1.3,
                        fontFamily: headingsFont,
                        minHeight: '60px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {voucher?.name || voucher?.campaignName || 'Special Voucher Campaign'}
                </Typography>

                {/* Description */}
                <Typography
                    sx={{
                        color: '#5a5a5a',
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.6,
                        fontFamily: bodyFont,
                        fontWeight: 500,
                        minHeight: '60px',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {voucher?.description || 'Grab exclusive eVouchers at amazing prices. Limited stock available!'}
                </Typography>

                {/* Countdown Timer */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        marginTop: 'auto',
                    }}
                >
                    {[
                        { value: timeRemaining.days, label: 'Days' },
                        { value: timeRemaining.hours, label: 'Hrs' },
                        { value: timeRemaining.minutes, label: 'Mins' },
                        { value: timeRemaining.seconds, label: 'Secs' },
                    ].map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <Box
                                sx={{
                                    background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                                    borderRadius: '8px',
                                    padding: '8px 10px',
                                    minWidth: '48px',
                                    textAlign: 'center',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                                    border: '1px solid rgba(0, 0, 0, 0.05)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)`,
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: primaryColor,
                                        fontSize: '20px',
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {String(item.value).padStart(2, '0')}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    color: '#707070',
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    fontFamily: bodyFont,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Action Button */}
                <Button
                    onClick={() => onSelectVoucher(voucher)}
                    disabled={isExpired}
                    variant="contained"
                    fullWidth
                    sx={{
                        background: isExpired
                            ? 'linear-gradient(135deg, #e0e0e0 0%, #d0d0d0 100%)'
                            : `linear-gradient(135deg, ${buttonPrimaryColor} 0%, ${buttonPrimaryColor}dd 100%)`,
                        color: isExpired ? '#999' : '#fff',
                        fontSize: { xs: '14px', md: '15px' },
                        fontWeight: 700,
                        borderRadius: `${buttonRadius}px`,
                        padding: { xs: '12px', md: '14px' },
                        textTransform: 'none',
                        fontFamily: bodyFont,
                        marginTop: 1,
                        boxShadow: isExpired
                            ? 'none'
                            : `0 6px 20px ${buttonPrimaryColor}40`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                            transition: 'left 0.5s',
                        },
                        '&:hover': {
                            background: isExpired
                                ? 'linear-gradient(135deg, #e0e0e0 0%, #d0d0d0 100%)'
                                : `linear-gradient(135deg, ${buttonPrimaryColor}dd 0%, ${buttonPrimaryColor} 100%)`,
                            transform: isExpired ? 'none' : 'translateY(-2px)',
                            boxShadow: isExpired
                                ? 'none'
                                : `0 8px 24px ${buttonPrimaryColor}50`,
                            '&::before': {
                                left: '100%',
                            },
                        },
                        '&:disabled': {
                            cursor: 'not-allowed',
                        },
                    }}
                >
                    {isExpired ? 'Campaign Ended' : 'Select Voucher'}
                </Button>
            </CardContent>
        </Card>
    );
};

const EvoucherPage = () => {
    const [checkout, setCheckout] = useState(false);
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedVoucherData, setSelectedVoucherData] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentVoucher, setCurrentVoucher] = useState(null);
    const { colors, typography } = useSelector((state) => state.template);

    const primaryColor = colors?.primary || '#02b3ad';
    const headingsColor = colors?.headings || '#343434';
    const headingsFont = typography?.headings || 'Poppins';
    const bodyFont = typography?.body || 'Poppins';

    const getVoucherss = () => {
        setLoading(true);
        let businessNumber = secureLocalStorage.getItem("businessId");
        HttpRequest({
            method: 'GET',
            url: `/api/v1/ecommerce/voucher_campaigns?businessNumber=${businessNumber}&limit=100&page=1&status=Active`
        }).then(async (data) => {
            const vouchersFound = data.response.data || [];
            // Filter only active vouchers that haven't expired
            const activeVouchers = vouchersFound.filter(voucher => {
                const endDate = new Date(voucher.endDate).getTime();
                const now = new Date().getTime();
                return endDate > now;
            });
            setVouchers(activeVouchers);
        }).catch(() => {
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        getVoucherss();
    }, []);

    const handleSelectVoucher = (voucher) => {
        setCurrentVoucher(voucher);
        setModalOpen(true);
    };

    const handleProceedToCheckout = (selectedAmount) => {
        setSelectedVoucherData({
            voucherDetails: currentVoucher,
            selectedVoucher: selectedAmount
        });
        setCheckout(true);
        window.scrollTo(0, 0);
    };

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                background: '#fbfbfb',
                padding: { xs: '20px', sm: '30px', md: '40px' },
            }}
        >
            {checkout ? (
                <EvoucherCheckout
                    voucherData={selectedVoucherData}
                    setVoucherData={setSelectedVoucherData}
                    setCheckout={setCheckout}
                />
            ) : (
                <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
                    {/* Header Section */}
                    <Box sx={{ marginBottom: 4, textAlign: 'center' }}>
                        <Typography
                            variant="h3"
                            sx={{
                                color: headingsColor,
                                fontSize: { xs: '28px', sm: '36px', md: '42px' },
                                fontWeight: 800,
                                marginBottom: 2,
                                fontFamily: headingsFont,
                                background: `linear-gradient(135deg, ${headingsColor} 0%, ${headingsColor}cc 100%)`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                            }}
                        >
                            Available eVouchers
                        </Typography>
                        <Typography
                            sx={{
                                color: '#5a5a5a',
                                fontSize: { xs: '14px', sm: '16px' },
                                fontFamily: bodyFont,
                                maxWidth: '600px',
                                margin: '0 auto',
                                lineHeight: 1.6,
                            }}
                        >
                            Choose from our exclusive collection of eVouchers. Perfect for gifting or treating yourself!
                        </Typography>
                    </Box>

                    {/* Vouchers Grid */}
                    {loading ? (
                        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                            {[1, 2, 3, 4, 5, 6].map((item) => (
                                <Grid item xs={12} sm={6} md={4} key={item}>
                                    <VoucherGridCardSkeleton />
                                </Grid>
                            ))}
                        </Grid>
                    ) : vouchers.length > 0 ? (
                        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                            {vouchers.map((voucher, index) => (
                                <Grid item xs={12} sm={6} md={4} key={voucher._id || index}>
                                    <VoucherGridCard
                                        voucher={voucher}
                                        onSelectVoucher={handleSelectVoucher}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                padding: '60px 20px',
                                background: 'linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.01) 100%)',
                                borderRadius: '20px',
                                border: '2px dashed rgba(0,0,0,0.1)',
                            }}
                        >
                            <LocalOffer
                                sx={{
                                    fontSize: 80,
                                    color: 'rgba(0,0,0,0.2)',
                                    marginBottom: 2
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#707070',
                                    fontSize: '18px',
                                    fontFamily: bodyFont,
                                    fontWeight: 600,
                                }}
                            >
                                No active vouchers available at the moment
                            </Typography>
                            <Typography
                                sx={{
                                    color: '#999',
                                    fontSize: '14px',
                                    fontFamily: bodyFont,
                                    marginTop: 1,
                                }}
                            >
                                Check back soon for exciting offers!
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Amount Selection Modal */}
            <VoucherAmountModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                voucher={currentVoucher}
                onProceed={handleProceedToCheckout}
            />
        </Box>
    )
}
export default EvoucherPage
